import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  Renderer2,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MainProductCardComponent } from '@components/cards/main-product-card/main-product-card.component';
import { LnChevronLeftSvgIconComponent } from '@components/svg-icons/ln-chevron-left-svg-icon/ln-chevron-left-svg-icon.component';
import { LnChevronRightSvgIconComponent } from '@components/svg-icons/ln-chevron-right-svg-icon/ln-chevron-right-svg-icon.component';
import { SpecialProductInterface } from '@interfaces/products/special_product_interface';

@Component({
  selector: 'ln-products-carousel',
  imports: [MainProductCardComponent, LnChevronLeftSvgIconComponent, LnChevronRightSvgIconComponent],
  templateUrl: './ln-products-carousel.component.html',
  styleUrl: './ln-products-carousel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LnProductsCarouselComponent implements AfterViewInit {
  TAG = 'LnProductsCarouselComponent';
  @Input({ required: true }) products: WritableSignal<SpecialProductInterface[]> = signal([]);
  @Input({ required: true }) showProductsPrice!: boolean;
  @ViewChild('carrouselNativeElement', { static: false })
  scrollList!: ElementRef<HTMLDivElement>;
  private readonly CHEVRONS_SIZES = 64;
  private readonly MAX_COLS = 6;
  private readonly MAX_MOBILE_COLS = 2;
  private readonly SM_DEVICES_SCREEN_WIDTH = 640;
  private readonly CARROUSEL_ITEMS_DIVIDER_WIDTH_MOBILE = 15;
  private readonly CARROUSEL_ITEMS_DIVIDER_WIDTH_SM_DEVICES = 40;
  showArrowLeft = signal(false);
  showArrowRight = signal(false);
  CARROUSEL_ITEMS_DIVIDER_WIDTH = signal(42);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);
  ngAfterViewInit() {
    this.setCarouselItemsDividerWidth(
      window.innerWidth > this.SM_DEVICES_SCREEN_WIDTH
        ? this.CARROUSEL_ITEMS_DIVIDER_WIDTH_SM_DEVICES
        : this.CARROUSEL_ITEMS_DIVIDER_WIDTH_MOBILE
    );
    this.ngZone.onStable.subscribe(() => {
      this.showOrHideRightButton();
    });
    this.ScrollListen();
  }

  @HostListener('window:resize', ['$event'])
  onScreenResize() {
    this.CheckWindowsResizeFinish();
  }

  CheckWindowsResizeFinish(): void {
    this.setCarouselItemsDividerWidth(
      window.innerWidth > this.SM_DEVICES_SCREEN_WIDTH
        ? this.CARROUSEL_ITEMS_DIVIDER_WIDTH_SM_DEVICES
        : this.CARROUSEL_ITEMS_DIVIDER_WIDTH_MOBILE
    );
    this.SetupDisplayLeftRightButtons();
  }

  ScrollListen() {
    this.renderer.listen(this.scrollList.nativeElement, 'scroll', () => {
      this.SetupDisplayLeftRightButtons();
    });
  }

  SetupDisplayLeftRightButtons() {
    const scroll = this.scrollList?.nativeElement;
    if (scroll !== null) {
      this.showArrowLeft.set(scroll.scrollLeft > 0);
      this.showOrHideRightButton();
    }
  }

  showOrHideRightButton() {
    const mobileWidth = window.innerWidth <= this.SM_DEVICES_SCREEN_WIDTH;
    const CHEVRONS_OFFSETS = mobileWidth ? this.CHEVRONS_SIZES / 2 : this.CHEVRONS_SIZES;
    this.showArrowRight.set(
      this.scrollList.nativeElement.scrollLeft + this.scrollList.nativeElement.clientWidth + CHEVRONS_OFFSETS <=
        this.scrollList.nativeElement.scrollWidth &&
        (mobileWidth ? this.products().length > this.MAX_MOBILE_COLS : this.products().length > this.MAX_COLS)
    );
  }

  LeftButtonClicked() {
    const scroll = this.scrollList.nativeElement;
    if (scroll !== null) {
      scroll.scrollBy({
        left: -scroll.clientWidth,
        behavior: 'smooth',
      });
    }
  }

  RightButtonClicked() {
    const scroll = this.scrollList.nativeElement;
    if (scroll !== null) {
      const maxScrollLeft = scroll.scrollWidth - scroll.clientWidth;
      const currentScroll = scroll.scrollLeft;
      if (currentScroll < maxScrollLeft) {
        scroll.scrollBy({
          left: scroll.clientWidth,
          behavior: 'smooth',
        });
      }
    }
  }
  setCarouselItemsDividerWidth(width: number): void {
    this.CARROUSEL_ITEMS_DIVIDER_WIDTH.set(width);
  }
}
